// ExecShell.js — ชิ้นส่วนที่ทุกหน้าในกลุ่ม "ตัวเลขและสถิติ" ของผู้บริหารใช้ร่วมกัน
//
// เหตุผลที่รวมไว้ที่เดียว: ทุกหน้ามีหัวเรื่อง ตัวเลือกปีการศึกษา ตัวเลือกช่วงวันที่
// และใบตัวเลขที่มี "ตัวเทียบ" เหมือนกันหมด ถ้าแต่ละหน้าทำเอง รูปแบบจะเพี้ยนกัน
// ทีละนิดจนดูเหมือนคนละระบบ
//
// ปีการศึกษาเก็บใน URL (?year_id=) ไม่ใช่ state ของหน้า เพื่อให้ลิงก์ที่ผู้บริหาร
// กดจากหน้าภาพรวมพาไปที่ปีเดียวกัน และกด back กลับมาได้ค่าเดิม
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, Form, Spinner, Alert } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";

export const toNum = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

export const fmtNum = (value) => toNum(value).toLocaleString("th-TH");
export const fmtPct = (value) => (value === null || value === undefined ? "—" : `${toNum(value)}%`);
export const fmtBaht = (value) => `฿${toNum(value).toLocaleString("th-TH", { maximumFractionDigits: 0 })}`;

export const MONTH_NAMES = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

// สีของระดับความเสี่ยง ใช้ชุดเดียวกับหน้าติดตามนักเรียนเพื่อให้ครูกับผู้บริหาร
// เห็นสีเดียวกันหมายถึงเรื่องเดียวกัน
export const RISK_COLORS = { red: "#dc3545", orange: "#fd7e14", yellow: "#ffc107", green: "#198754" };
export const RISK_LABELS = { red: "เสี่ยงสูง", orange: "เสี่ยง", yellow: "เฝ้าระวัง", green: "ปกติ" };

export const fmtThaiDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
};

export function EmptyState({ message = "ยังไม่มีข้อมูลสำหรับแสดงผล", compact = false }) {
  return (
    <div className={`text-center text-secondary ${compact ? "py-3" : "py-5"}`}>
      <i className="ri-inbox-line d-block mb-2" style={{ fontSize: compact ? 24 : 36 }} />
      <span className={compact ? "fs-xs" : ""}>{message}</span>
    </div>
  );
}

// ใบตัวเลขของหน้าผู้บริหาร ต่างจาก KpiCard เดิมตรงที่บังคับให้มีที่สำหรับ "ตัวเทียบ"
// ตัวเลขโดดๆ ตอบไม่ได้ว่าดีขึ้นหรือแย่ลง ซึ่งเป็นคำถามเดียวที่ผู้บริหารถาม
export function StatCard({ icon, color = "primary", label, value, sub, compare, to, tone }) {
  const delta = compare?.delta;
  const deltaTone = delta === undefined || delta === null || delta === 0
    ? "secondary"
    : (compare.higherIsBetter === false ? (delta > 0 ? "danger" : "success") : (delta > 0 ? "success" : "danger"));

  const body = (
    <Card className={`card-one h-100 ${tone ? `border-${tone}` : ""}`}>
      <Card.Body className="d-flex align-items-start gap-3">
        {icon && (
          <div className={`avatar bg-${color} text-white rounded-circle flex-shrink-0`} style={{ width: 42, height: 42 }}>
            <i className={icon} style={{ fontSize: 22 }} />
          </div>
        )}
        <div className="flex-fill min-w-0">
          <h3 className="card-value mb-0">{value}</h3>
          <label className="card-title fs-sm text-secondary mb-0 d-block text-truncate">{label}</label>
          {sub && <div className="fs-xs text-secondary mt-1">{sub}</div>}
          {compare && (
            <div className={`fs-xs mt-1 text-${deltaTone}`}>
              {delta !== undefined && delta !== null && delta !== 0 && (
                <i className={`ri-arrow-${delta > 0 ? "up" : "down"}-line me-1`} />
              )}
              {compare.label}
            </div>
          )}
        </div>
        {to && <i className="ri-arrow-right-s-line text-secondary flex-shrink-0" style={{ fontSize: 20 }} />}
      </Card.Body>
    </Card>
  );

  return to ? <Link to={to} className="text-decoration-none text-body d-block h-100">{body}</Link> : body;
}

// แถบแสดงสัดส่วนแบบไม่ใช้ไลบรารีกราฟ — เบากว่าและอ่านง่ายกว่าสำหรับตัวเลข 3–5 ค่า
export function StackedBar({ segments, height = 10 }) {
  const total = segments.reduce((sum, s) => sum + toNum(s.value), 0);
  if (total <= 0) return <div className="bg-light rounded" style={{ height }} />;
  return (
    <div className="d-flex rounded overflow-hidden" style={{ height }}>
      {segments.map((s, index) => (
        toNum(s.value) > 0 && (
          <div
            key={s.key || index}
            title={`${s.label}: ${fmtNum(s.value)}`}
            style={{ width: `${(toNum(s.value) / total) * 100}%`, background: s.color }}
          />
        )
      ))}
    </div>
  );
}

// ตารางที่เลื่อนแนวนอนได้ในกล่องของตัวเอง ไม่ให้ทั้งหน้าเลื่อนซ้ายขวาบนมือถือ
export function ScrollTable({ children }) {
  return <div className="table-responsive">{children}</div>;
}

/* ── ตัวเลือกร่วม ────────────────────────────────────────────────────────── */

// ปีการศึกษาใช้ร่วมกันทุกหน้า จึงเก็บใน query string ของ URL
export function useExecYear() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [years, setYears] = useState([]);
  const yearId = searchParams.get("year_id") || "";

  useEffect(() => {
    let mounted = true;
    axiosInstance.get("/api/executive/stats/years")
      .then((res) => { if (mounted) setYears(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (mounted) setYears([]); });
    return () => { mounted = false; };
  }, []);

  const setYearId = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("year_id", next); else params.delete("year_id");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  return { years, yearId, setYearId };
}

export function YearPicker({ years, yearId, onChange, activeYear }) {
  return (
    <Form.Select
      size="sm"
      style={{ width: "auto", minWidth: 160 }}
      value={yearId || activeYear?.year_id || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      {years.length === 0 && activeYear && (
        <option value={activeYear.year_id}>ปีการศึกษา {activeYear.year_year}</option>
      )}
      {years.map((y) => (
        <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
      ))}
    </Form.Select>
  );
}

// ช่วงวันที่แบบปุ่มสำเร็จรูป — ผู้บริหารเลือก "30 วัน" บ่อยกว่าจิ้มปฏิทินเอง
const RANGE_PRESETS = [
  { key: "7", label: "7 วัน", days: 7 },
  { key: "30", label: "30 วัน", days: 30 },
  { key: "90", label: "1 ภาคเรียน", days: 90 },
];

// วันที่ในรูปแบบ YYYY-MM-DD ตามเวลาเครื่อง — ต้องแยกส่วนเอง ไม่ใช้ new Date(string)
// แล้ว setDate เพราะสตริงแบบนี้ถูกอ่านเป็น UTC วันที่ที่ได้กลับมาจะเลื่อนไปหนึ่งวัน
export const todayKey = () => new Date().toLocaleDateString("en-CA");

export const shiftDay = (value, days) => {
  const [y, m, d] = String(value || "").split("-").map(Number);
  if (!y || !m || !d) return todayKey();
  return new Date(y, m - 1, d + days).toLocaleDateString("en-CA");
};

export const fmtThaiFullDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = String(value).split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("th-TH", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

// ตัวเลือกช่วงเวลาของหน้าการเข้าเรียน — มีโหมด "รายวัน" เพิ่มจาก RangePicker
// เพราะคำถามประจำเช้าของผู้บริหารคือ "เมื่อวานเป็นอย่างไร" ซึ่งค่าเฉลี่ย 30 วัน
// ตอบไม่ได้ ปุ่มลูกศรมีไว้ไล่ดูวันต่อวันโดยไม่ต้องเปิดปฏิทินทุกครั้ง
//
// ห้ามเลื่อนเลยวันนี้ — วันข้างหน้ายังไม่มีการเช็คชื่อ หน้าจะว่างเปล่าโดยไม่มีเหตุผล
// ให้ผู้ใช้เดาว่าพังหรือไม่มีข้อมูล
export function PeriodPicker({ mode, rangeKey, day, onRange, onDay }) {
  const today = todayKey();
  const atToday = !day || day >= today;

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <div className="btn-group btn-group-sm" role="group" aria-label="ช่วงเวลา">
        <button
          type="button"
          className={`btn btn-${mode === "day" && day === today ? "primary" : "outline-secondary"}`}
          onClick={() => onDay(today)}
        >
          วันนี้
        </button>
        {RANGE_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className={`btn btn-${mode === "range" && rangeKey === preset.key ? "primary" : "outline-secondary"}`}
            onClick={() => onRange(preset.key, preset.days)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="input-group input-group-sm" style={{ width: "auto" }}>
        <button
          type="button"
          className="btn btn-outline-secondary"
          title="วันก่อนหน้า"
          aria-label="วันก่อนหน้า"
          onClick={() => onDay(shiftDay(day || today, -1))}
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <Form.Control
          type="date"
          size="sm"
          value={day || today}
          max={today}
          style={{ width: 150 }}
          onChange={(e) => e.target.value && onDay(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          title="วันถัดไป"
          aria-label="วันถัดไป"
          disabled={atToday}
          onClick={() => onDay(shiftDay(day || today, 1))}
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    </div>
  );
}

export function RangePicker({ value, onChange }) {
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label="ช่วงเวลา">
      {RANGE_PRESETS.map((preset) => (
        <button
          key={preset.key}
          type="button"
          className={`btn btn-${value === preset.key ? "primary" : "outline-secondary"}`}
          onClick={() => onChange(preset.key, preset.days)}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}

/* ── โครงหน้า ────────────────────────────────────────────────────────────── */

export function ExecPage({ title, lead, icon, activeYear, toolbar, loading, error, children }) {
  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-start justify-content-between mb-4 gap-3">
          <div className="min-w-0">
            <label className="main-title-label">ตัวเลขและสถิติ</label>
            <h2 className="main-title mb-1 d-flex align-items-center gap-2">
              {icon && <i className={icon} />}
              {title}
            </h2>
            {lead && <p className="text-secondary mb-0">{lead}</p>}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mt-3 mt-sm-0">
            {toolbar}
            <Badge bg="light" text="dark" className="fw-normal">อ่านอย่างเดียว</Badge>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-secondary mt-3">กำลังโหลดข้อมูล...</div>
          </div>
        )}

        {!loading && error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && children}

        <Footer />
      </div>
    </React.Fragment>
  );
}

// หัวข้อคั่นภายในหน้า
export function SectionTitle({ icon, title, note, color = "primary" }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-3 mt-1 flex-wrap">
      {icon && <i className={`${icon} text-${color}`} style={{ fontSize: 20 }} />}
      <h5 className="mb-0">{title}</h5>
      {note && <span className="fs-xs text-secondary">{note}</span>}
    </div>
  );
}

// action = ตัวเลือกของกล่องนั้นเอง (เช่น สลับรายเดือน/รายวัน) วางชิดขวาในหัวกล่อง
// ไม่ใช่บน toolbar ของหน้า เพราะมีผลกับกราฟกล่องเดียว ไม่ใช่ทั้งหน้าแบบตัวเลือกปี
export function Panel({ title, note, action, children, className = "", bodyClassName = "" }) {
  return (
    <Card className={`card-one h-100 ${className}`}>
      {(title || note || action) && (
        <Card.Header className="justify-content-between gap-2 flex-wrap">
          {/* หัวข้อกับหมายเหตุอยู่แถวเดียวกันเหมือนเดิม เพิ่มแค่กล่องครอบเพื่อให้
              action ถูกดันไปชิดขวาได้ ไม่ใช่ไหลต่อจากหมายเหตุ */}
          <div className="d-flex align-items-center gap-2 flex-wrap min-w-0">
            {title && <Card.Title as="h6">{title}</Card.Title>}
            {note && <div className="fs-xs text-secondary">{note}</div>}
          </div>
          {action}
        </Card.Header>
      )}
      <Card.Body className={bodyClassName}>{children}</Card.Body>
    </Card>
  );
}

// โหลดข้อมูลของหน้า — คืน { data, loading, error } และโหลดใหม่เมื่อ query เปลี่ยน
export function useExecData(path, query = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const queryKey = useMemo(() => JSON.stringify(query), [query]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    Object.entries(JSON.parse(queryKey)).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") params.set(key, value);
    });
    const qs = params.toString();

    axiosInstance.get(`${path}${qs ? `?${qs}` : ""}`)
      .then((res) => { if (mounted) setData(res.data || null); })
      .catch((err) => {
        console.error(`Executive stats fetch error (${path}):`, err);
        if (mounted) setError(err?.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้");
      })
      .finally(() => { if (mounted) setLoading(false); });

    return () => { mounted = false; };
  }, [path, queryKey]);

  return { data, loading, error };
}

// ส่วนต่างระหว่างปีนี้กับปีก่อน พร้อมข้อความพร้อมใช้
export function compareTo(current, previous, { suffix = "", higherIsBetter = true } = {}) {
  if (previous === null || previous === undefined) return null;
  const delta = toNum(current) - toNum(previous);
  const sign = delta > 0 ? "+" : "";
  return {
    delta,
    higherIsBetter,
    label: delta === 0
      ? `เท่ากับปีก่อน (${fmtNum(previous)}${suffix})`
      : `${sign}${fmtNum(delta)}${suffix} จากปีก่อน (${fmtNum(previous)}${suffix})`,
  };
}
